import { VisuallyHidden } from "./VisuallyHidden";
import { maskProductName } from "../lib/mask";
import styles from "./StandingRow.module.css";

/**
 * One standing bid in tomorrow's auction: a projected rank, a masked name, an amount.
 *
 * Deliberately **not** `LedgerRow` (`WEB-011`). That row exists to send a reader to a deal,
 * and this one exists to send them nowhere: a bid that has not won anything buys no
 * exposure, so the standings carry no link, no description, no badge and no destination
 * host. Sharing a component with the ranked lists would have made "no link here" a prop
 * that a later change could quietly flip back.
 *
 * The mask is `first two · *** · last two` (`src/lib/mask.ts`). It is enough for a founder
 * to recognise their own bid in the list — canon § 14 gives them the authoritative view in
 * the seller app either way — and not enough to be an advertisement.
 *
 * A row is a list item and must be rendered inside an ordered list, which is what carries
 * the projected order to assistive technology.
 */
export function StandingRow({
  rank,
  productName,
  figure,
  figureLabel,
  muted = false,
}: {
  rank: number;
  /** Core's real name. Masked here, never rendered whole. */
  productName: string;
  /** The already-formatted amount, e.g. "$15". */
  figure: string;
  /** What that number is. Always rendered, never implied. */
  figureLabel: string;
  /** Dimmed styling for a bid that is not currently winning a position. */
  muted?: boolean | undefined;
}) {
  return (
    <li className={[styles.row, muted ? styles.muted : null].filter(Boolean).join(" ")}>
      <span className={`${styles.rank} figure`} aria-hidden="true">
        {String(rank).padStart(2, "0")}
      </span>
      <span className={styles.bidder}>
        <VisuallyHidden>Projected position {String(rank)}: </VisuallyHidden>
        {/*
          The mask is text, not a styling effect, so it survives copy-and-paste, a reader
          mode and a screen reader alike. There is nothing underneath it to reveal.
        */}
        {maskProductName(productName)}
      </span>
      <span className={styles.figure}>
        <span className={`${styles.amount} figure`}>{figure}</span>
        <span className={styles.figureLabel}>{figureLabel}</span>
      </span>
    </li>
  );
}
